/// Unified sign-in / sign-up surface. Three modes:
///
///   • Sign in     — Google + email/password. Has "Forgot password?"
///   • Sign up     — email/password + optional display name. Sends
///                    a verification email after the link/create.
///   • Reset       — single-field email form, dispatches the
///                    Firebase password-reset email.
///
/// Three providers wired: Google, Apple, and email/password. Apple
/// requires the Service ID + .p8 key to be configured in Firebase
/// Console → Authentication → Sign-in method → Apple before the
/// button works in production; until that flip, attempting it
/// returns `auth/operation-not-allowed` and the modal surfaces an
/// "Apple sign-in is not enabled" inline error.

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useProfileStore } from '../state/profileStore'

type Mode = 'signin' | 'signup' | 'reset'

export function AuthModal(props: {
  /// Initial mode the modal opens in. Defaults to 'signin'.
  initialMode?: Mode
  /// Optional copy override for the header — e.g., "Save your
  /// progress" or "Sign in to purchase."
  eyebrow?: string
  title?: string
  onDismiss: () => void
  /// Called after a successful sign-in / sign-up so the caller
  /// (paywall, nudge, etc.) can react. The modal closes
  /// automatically on success.
  onSignedIn?: () => void
}) {
  const [mode, setMode] = useState<Mode>(props.initialMode ?? 'signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const signInWithGoogle = useProfileStore((s) => s.signInWithGoogle)
  const signInWithApple = useProfileStore((s) => s.signInWithApple)
  const signInWithEmail = useProfileStore((s) => s.signInWithEmail)
  const signUpWithEmail = useProfileStore((s) => s.signUpWithEmail)
  const sendPasswordReset = useProfileStore((s) => s.sendPasswordReset)

  const eyebrow = props.eyebrow ?? 'Account'
  const title =
    props.title ??
    (mode === 'signup'
      ? 'Create your account'
      : mode === 'reset'
        ? 'Reset your password'
        : 'Sign in to FlatoutATC')

  const dismissOnSuccess = () => {
    props.onSignedIn?.()
    props.onDismiss()
  }

  const handleGoogle = async () => {
    setBusy(true)
    setError(null)
    setInfo(null)
    const result = await signInWithGoogle()
    if (result.ok) {
      dismissOnSuccess()
      return
    }
    if (result.reason === 'cancelled') {
      setBusy(false)
      return
    }
    setError(result.message)
    setBusy(false)
  }

  const handleApple = async () => {
    setBusy(true)
    setError(null)
    setInfo(null)
    const result = await signInWithApple()
    if (result.ok) {
      dismissOnSuccess()
      return
    }
    if (result.reason === 'cancelled') {
      setBusy(false)
      return
    }
    setError(result.message)
    setBusy(false)
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setInfo(null)
    if (mode === 'signup') {
      const result = await signUpWithEmail({ email, password, displayName })
      if (result.ok) {
        if (result.verificationSent) {
          setInfo('Check your email — we sent you a verification link.')
        }
        // Wait a beat so the success message is visible, then close.
        window.setTimeout(dismissOnSuccess, 1200)
        return
      }
      setError(result.message)
      setBusy(false)
      return
    }
    if (mode === 'reset') {
      const result = await sendPasswordReset(email)
      if (result.ok) {
        setInfo('If an account exists for that email, we’ve sent a reset link.')
        setBusy(false)
        return
      }
      setError(result.message)
      setBusy(false)
      return
    }
    // Sign in
    const result = await signInWithEmail({ email, password })
    if (result.ok) {
      dismissOnSuccess()
      return
    }
    setError(result.message)
    setBusy(false)
  }

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-50 overflow-y-auto bg-black/70 px-4 py-6 backdrop-blur-sm"
      onClick={busy ? undefined : props.onDismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mx-auto w-full max-w-sm rounded-xl bg-slate-900 p-6 text-slate-100 shadow-2xl ring-1 ring-white/10"
      >
        <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300/80">
          {eyebrow}
        </div>
        <h2 className="mt-1 text-lg font-semibold tracking-wide">{title}</h2>

        {mode !== 'reset' && (
          <>
            <button
              onClick={handleGoogle}
              disabled={busy}
              className={
                'mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow ring-1 ring-white/20 ' +
                (busy ? 'opacity-60' : 'hover:bg-slate-100')
              }
            >
              <GoogleMark />
              <span>Continue with Google</span>
            </button>
            <button
              onClick={handleApple}
              disabled={busy}
              className={
                'mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-black px-4 py-2 text-sm font-semibold text-white shadow ring-1 ring-white/15 ' +
                (busy ? 'opacity-60' : 'hover:bg-zinc-900')
              }
            >
              <AppleMark />
              <span>Continue with Apple</span>
            </button>
            <div className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-wider text-slate-500">
              <div className="h-px flex-1 bg-white/10" />
              <span>Or with email</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>
          </>
        )}

        <form onSubmit={handleEmailSubmit} className="space-y-2">
          <label className="block">
            <span className="text-[11px] font-semibold text-slate-400">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              className="mt-1 w-full rounded-md bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-emerald-500"
              placeholder="you@example.com"
            />
          </label>
          {mode !== 'reset' && (
            <label className="block">
              <span className="text-[11px] font-semibold text-slate-400">Password</span>
              <input
                type="password"
                required
                minLength={6}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                className="mt-1 w-full rounded-md bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-emerald-500"
                placeholder={mode === 'signup' ? 'At least 6 characters' : ''}
              />
            </label>
          )}
          {mode === 'signup' && (
            <label className="block">
              <span className="text-[11px] font-semibold text-slate-400">
                Display name <span className="text-slate-500">(optional)</span>
              </span>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={busy}
                maxLength={50}
                className="mt-1 w-full rounded-md bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-emerald-500"
                placeholder="Shown on leaderboards"
              />
            </label>
          )}

          {error && (
            <div className="rounded-md bg-rose-900/50 px-3 py-2 text-xs text-rose-100 ring-1 ring-rose-500/30">
              {error}
            </div>
          )}
          {info && (
            <div className="rounded-md bg-emerald-900/40 px-3 py-2 text-xs text-emerald-100 ring-1 ring-emerald-500/30">
              {info}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className={
              'mt-2 w-full rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow ' +
              (busy ? 'opacity-60' : 'hover:bg-emerald-500')
            }
          >
            {busy
              ? 'Working…'
              : mode === 'signup'
                ? 'Create account'
                : mode === 'reset'
                  ? 'Send reset link'
                  : 'Sign in'}
          </button>
        </form>

        <div className="mt-4 flex items-center justify-between text-xs">
          <button
            onClick={() => {
              setError(null)
              setInfo(null)
              setMode(mode === 'signin' ? 'signup' : 'signin')
            }}
            disabled={busy || mode === 'reset'}
            className="text-slate-400 hover:text-white"
          >
            {mode === 'signup'
              ? 'Have an account? Sign in'
              : mode === 'signin'
                ? 'New here? Create account'
                : ''}
          </button>
          {mode !== 'reset' ? (
            <button
              onClick={() => {
                setError(null)
                setInfo(null)
                setMode('reset')
              }}
              disabled={busy}
              className="text-slate-400 hover:text-white"
            >
              Forgot password?
            </button>
          ) : (
            <button
              onClick={() => {
                setError(null)
                setInfo(null)
                setMode('signin')
              }}
              disabled={busy}
              className="text-slate-400 hover:text-white"
            >
              Back to sign in
            </button>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={props.onDismiss}
            disabled={busy}
            className="text-xs text-slate-400 hover:text-white"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function AppleMark() {
  // Standard Apple logo (path traced from Apple's brand assets;
  // simple enough to inline so we don't ship a font / asset).
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path
        fill="currentColor"
        d="M16.365 1.43c0 1.14-.46 2.23-1.21 3.02-.81.85-2.13 1.51-3.21 1.42-.13-1.1.42-2.27 1.13-3 .8-.83 2.21-1.46 3.29-1.44zm3.96 17.55c-.59 1.37-.88 1.99-1.65 3.2-1.06 1.69-2.55 3.79-4.39 3.81-1.64.02-2.06-1.07-4.28-1.06-2.22.01-2.69 1.08-4.33 1.06-1.84-.02-3.25-1.92-4.31-3.61C-1.4 17.42-1.71 11.62 1.18 8.7c1.34-1.37 3.13-2.18 5.11-2.21 2.04-.03 3.84 1.18 5.04 1.18 1.18 0 3.43-1.46 5.79-1.24.99.04 3.77.4 5.55 3.01-.14.09-3.32 1.94-3.28 5.78.05 4.59 4.03 6.12 4.07 6.14-.04.11-.65 2.21-2.13 4.62z"
      />
    </svg>
  )
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.5 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.5 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C40.9 35.7 44 30.3 44 24c0-1.2-.1-2.4-.4-3.5z"
      />
    </svg>
  )
}
